import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { t } from "@/lib/i18n";

/**
 * Editorial size-guide modal. Content is pulled from the merchant's own
 * product copy where possible (model-fit recommendations). We keep this
 * intentionally free of fabricated measurements — real cm charts should be
 * added once Marcini provides them.
 */
const MODEL_FITS = [
  { name: "Celina", height: "165 cm", size: "S" },
  { name: "Philip", height: "180 cm", size: "M" },
  { name: "Elias", height: "190 cm", size: "L" },
];

export default function SizeGuide({ open, onClose }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[90]"
            onClick={onClose}
            data-testid="size-guide-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.7, 0, 0.2, 1] }}
            className="fixed top-0 right-0 h-full w-full sm:w-[440px] bg-white z-[95] border-l border-[color:var(--marcini-border)] flex flex-col"
            data-testid="size-guide"
          >
            <div className="flex items-center justify-between px-5 md:px-6 h-16 border-b border-[color:var(--marcini-border)]">
              <span className="text-eyebrow">{t.pdp.sizeGuideTitle}</span>
              <button onClick={onClose} aria-label={t.common.close} className="p-2 -mr-2" data-testid="size-guide-close">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 md:px-8 py-8 space-y-10">
              {/* Model fits */}
              <section>
                <p className="text-eyebrow mb-4">{t.pdp.sizeGuideModelsLabel}</p>
                <div className="border-y border-[color:var(--marcini-border)] divide-y divide-[color:var(--marcini-border)]">
                  {MODEL_FITS.map((m) => (
                    <div key={m.name} className="grid grid-cols-[1fr_auto_auto] gap-6 py-5 md:py-6 items-baseline" data-testid={`size-guide-model-${m.name}`}>
                      <p className="font-serif-editorial text-2xl">{m.name}</p>
                      <p className="text-sm text-[color:var(--marcini-fg-2)]">{m.height}</p>
                      <p className="text-eyebrow">Str. {m.size}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Fit */}
              <section>
                <p className="text-eyebrow mb-3">{t.pdp.sizeGuideFitLabel}</p>
                <p className="text-[color:var(--marcini-fg-2)] leading-relaxed">
                  {t.pdp.sizeGuideFitBody}
                </p>
              </section>

              {/* Help */}
              <section className="pt-6 border-t border-[color:var(--marcini-border)]">
                <p className="text-eyebrow mb-3">{t.pdp.sizeGuideHelpLabel}</p>
                <p className="text-[color:var(--marcini-fg-2)] leading-relaxed">
                  {t.pdp.sizeGuideHelpBody}
                </p>
              </section>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
